import { afterNextRender, AfterViewInit, Component, computed, DestroyRef, ElementRef, inject, Input, OnInit, QueryList, signal, ViewChild, ViewChildren } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { UnsavedChangesModalComponent } from '@app/v2/shared/components/unsaved-changes-modal/unsaved-changes-modal.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { IndustrySector } from '@app/v2/shared/types/industry-sector.type'
import { AuthService } from '@auth/services/auth.service'
import { catchError, EMPTY, forkJoin, map, Observable, of, Subject, tap, throwError } from 'rxjs'
import { finalize, switchMap, take } from 'rxjs/operators'
import { EventWizardStep } from '../enums/event.enum'
import { EventService } from '../services/event.service'
import { Checkin, CheckinLocationPatchPayload, Event, EventTimingsResponse } from '../types/event.type'
import { EventIdentityComponent } from './event-identity/event-identity.component'
import { LocationTimeZoneComponent } from './location-time-zone/location-time-zone.component'
import { StepperComponent } from './stepper/stepper.component'

import { EventDatesAndTimingsComponent } from '../components/event-dates-and-timings/event-dates-and-timings.component'

@Component({
  selector: 'app-event-wizard',
  imports: [EventIdentityComponent, EventDatesAndTimingsComponent, StepperComponent, UnsavedChangesModalComponent, LocationTimeZoneComponent],
  templateUrl: './event-wizard.component.html',
  styleUrl: './event-wizard.component.scss',
  standalone: true,
  providers: [EventService]
})
export class EventWizardComponent implements OnInit, AfterViewInit {
  protected readonly UNSAVED_MODAL_ID = 'event-wizard-unsaved-changes-modal'

  @Input() eventId?: number
  @Input() organizationId?: number
  /** Set via componentInputs when opening the wizard (e.g. from timeline). Resolved on events/wizard routes. */
  @Input() set industrySectors(value: IndustrySector[] | undefined) {
    this._industrySectors.set(value ?? [])
  }

  @ViewChild('stepContainer', { static: true })
  private _stepContainer!: ElementRef<HTMLElement>

  @ViewChildren('stepEl')
  private _stepElements!: QueryList<ElementRef<HTMLElement>>

  @ViewChild(EventIdentityComponent)
  private _eventIdentityRef?: EventIdentityComponent

  @ViewChild(LocationTimeZoneComponent)
  private _locationTimeZoneRef?: LocationTimeZoneComponent

  readonly eventData = signal<Event | null>(null)
  readonly isLoading = signal<boolean>(false)
  readonly isSavingField = signal<boolean>(false)
  readonly currentStep = signal<EventWizardStep>(EventWizardStep.STEP_1)
  readonly totalSteps = 3

  private readonly _industrySectors = signal<IndustrySector[]>([])
  private readonly _previousStep = signal<EventWizardStep | null>(null)
  private readonly _isInitialLoad = signal<boolean>(true)
  private _observer?: IntersectionObserver
  readonly shouldAnimate = computed(() => !this._isInitialLoad())

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _eventService = inject(EventService)
  private readonly _alertService = inject(AlertService)
  private readonly _authService = inject(AuthService)
  private readonly _wizardService = inject(WizardService)
  private readonly _router = inject(Router)
  private _unregisterRouteWizard?: () => void

  /** Serializes checkin location PATCHes; a new emission cancels the previous in-flight request (e.g. rapid zoom). */
  private readonly _locationDetailsPatch$ = new Subject<CheckinLocationPatchPayload>()
  /** Prevents re-entrant silent-close while an auto short-name save is in flight. */
  private _silentCloseInProgress = false

  get industrySectorsSignal() {
    return this._industrySectors
  }

  constructor() {
    afterNextRender(() => {
      this._setupScrollObserver()
    })
    this._destroyRef.onDestroy(() => {
      this._observer?.disconnect()
      this._unregisterRouteWizard?.()
    })
  }

  ngOnInit(): void {
    this._listrenToLocationChanges()
    const organizationId = this.organizationId ?? this._authService.currentWorkspace?.id
    if (organizationId && this.eventId) this._loadEventData(organizationId, this.eventId)
  }

  ngAfterViewInit(): void {
    this._unregisterRouteWizard = this._wizardService.registerRouteWizard({
      hasUnsavedChanges: () => this.hasUnsavedChanges(),
      cancelPendingBlurSave: () => this.cancelPendingBlurSave(),
      unsavedModalId: this.UNSAVED_MODAL_ID
    })
  }

  /**
   * Setup the scroll observer to detect the visible step.
   * Uses the step container as root so intersection is relative to the scroll area (mouse/trackpad scroll).
   */
  private _setupScrollObserver(): void {
    if (this._observer) return

    const container = this._stepContainer?.nativeElement
    if (!container) return

    this._observer = new IntersectionObserver((entries) => this._handleStepIntersection(entries), {
      root: container,
      threshold: 0.5
    })

    const stepEls = this._stepElements?.length > 0 ? this._stepElements.toArray().map((el) => el.nativeElement) : Array.from(container.querySelectorAll<HTMLElement>('[data-step]'))
    stepEls.forEach((el) => this._observer!.observe(el))
  }

  /**
   * Handle the step intersection
   * @param entries {IntersectionObserverEntry[]} - The intersection observer entries
   * @returns {void}
   */
  private _handleStepIntersection(entries: IntersectionObserverEntry[]): void {
    const visible = entries.find((e) => e.isIntersecting)
    if (!visible) return

    const stepNumber = this._extractStepNumber(visible.target as HTMLElement)
    const step = stepNumber as EventWizardStep

    if (!Object.values(EventWizardStep).includes(step) || step === this.currentStep()) return

    this._setCurrentStep(step)
  }

  /**
   * Extract the step number from the element id
   * @param element {Element} - The element to extract the step number from
   * @returns {EventWizardStep} - The step number
   */
  private _extractStepNumber(element: HTMLElement): EventWizardStep {
    return Number(element.dataset['step'])
  }

  /**
   * Set the current step
   * @param step {EventWizardStep} - The step to set
   * @returns {void}
   */
  private _setCurrentStep(step: EventWizardStep): void {
    this._previousStep.set(this.currentStep())
    this.currentStep.set(step)
    this._isInitialLoad.set(false)
  }

  /**
   * Handle step change from stepper component
   * @param step {number} - The step number (1-indexed)
   * @returns {void}
   */
  public onStepChanged(step: number): void {
    const wizardStep = step as EventWizardStep
    if (!Object.values(EventWizardStep).includes(wizardStep)) return

    this._setCurrentStep(step)
    const container = this._stepContainer?.nativeElement
    if (!container) return
    const stepEl = container.querySelector<HTMLElement>(`[data-step="${wizardStep}"]`)
    stepEl?.scrollIntoView({ block: 'start', behavior: 'auto' })
  }

  /**
   * Load event data from the API
   * @param organizationId {number} - The organization ID
   * @param eventId {number} - The event ID
   * @returns {void}
   */
  private _loadEventData(organizationId: number, eventId: number): void {
    this.isLoading.set(true)
    forkJoin({
      event: this._eventService.getEvent(organizationId, eventId),
      timings: this._eventService.getEventTimings(organizationId, eventId).pipe(catchError(() => of(null as Response<EventTimingsResponse> | null)))
    })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ({ event, timings }) => {
          const ev = event.data
          const tr = timings?.data
          const merged: Event = {
            ...ev,
            ...(tr
              ? {
                  public_start_date: tr.public_start_date ?? ev.public_start_date,
                  public_end_date: tr.public_end_date ?? ev.public_end_date,
                  is_indefinitely: tr.is_indefinitely ?? ev.is_indefinitely,
                  timings: tr.timings != null ? tr.timings : ev.timings ?? null
                }
              : {})
          }
          this.eventData.set(merged)
          this.isLoading.set(false)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error.message || 'Failed to load event')
          this.isLoading.set(false)
        }
      })
  }

  /**
   * Handle field change from event-identity component (save on blur).
   * Only sends a PATCH request if the value actually changed compared to current eventData.
   * @param change { { field: string; value: string | number[] } } - The field and value that changed
   * @returns { void } - The void return type
   */
  private _updateEventDataObservable(change: { field: string; value: string | number[] }, options?: { throwOnValidationError?: boolean }): import('rxjs').Observable<void> {
    const current = this.eventData()
    if (!current) return of(undefined)

    const currentValue = (current as Record<string, unknown>)[change.field]
    if (typeof currentValue === 'string') {
      const incomingValue = String(change.value ?? '').trim()
      if ((currentValue || '').trim() === incomingValue) return of(undefined)
    } else if (Array.isArray(currentValue) && Array.isArray(change.value)) {
      if (currentValue.length === change.value.length && currentValue.every((v, i) => v === change.value[i])) return of(undefined)
    } else if (currentValue === change.value) {
      return of(undefined)
    }

    const isBannerField = change.field === 'cover_page'
    if (!isBannerField) this.isSavingField.set(true)

    return this._eventService.updateEventData(current.workspace_id, current.id, { [change.field]: change.value }).pipe(
      tap((response: Response<Event>) => {
        // Clear any previous server validation error on the updated field
        if (!isBannerField) {
          const control = this._eventIdentityRef?.eventIdentityForm().get(change.field)
          const controlErrors = control?.errors
          if (control && controlErrors && controlErrors['serverError']) {
            const { serverError, ...rest } = controlErrors
            control.setErrors(Object.keys(rest).length ? rest : null)
          }
        }

        this.eventData.set({ ...this.eventData(), ...response.data })
        if (change.field === 'name') {
          const title = String(response.data?.name ?? change.value ?? '').trim()
          const breadcrumbs = title ? [title] : ['Event Name']
          this._wizardService.setBreadcrumbs(breadcrumbs)
        }
        if (isBannerField) this._alertService.alert(Alert.SUCCESS, response.message)
        else this.isSavingField.set(false)
        this.isLoading.set(false)
      }),
      map(() => undefined),
      catchError((error) => {
        const status = error?.status
        const serverBody = error?.error

        if (!isBannerField) this.isSavingField.set(false)
        this.isLoading.set(false)

        // 422 = validation error. Apply per-field server errors to the identity form.
        if (status === 422 && serverBody && typeof serverBody === 'object' && !isBannerField) {
          this._eventIdentityRef?.setServerErrors((serverBody.errors ?? serverBody) as Record<string, string[]>)
          if (options?.throwOnValidationError) return throwError(() => error)
          return of(undefined)
        }

        const message = serverBody?.message || error?.message || 'Failed to update event'
        this._alertService.alert(Alert.ERROR, message)
        if (options?.throwOnValidationError) return throwError(() => error)
        return of(undefined)
      })
    )
  }

  /**
   * Handle field change from event-identity component (save on blur).
   * Only sends a PATCH request if the value actually changed compared to current eventData.
   */
  public updateEventData(change: { field: string; value: string | number[] }): void {
    this._updateEventDataObservable(change).pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Whether any tracked field (event identity or location) has unsaved changes.
   */
  public hasUnsavedChanges(): boolean {
    return (this._eventIdentityRef?.hasUnsavedChanges?.() ?? false) || (this._locationTimeZoneRef?.hasUnsavedChanges?.() ?? false)
  }

  /**
   * Cancel pending blur-triggered saves before showing unsaved-changes modal.
   */
  public cancelPendingBlurSave(): void {
    this._eventIdentityRef?.cancelPendingBlurSave?.()
    this._locationTimeZoneRef?.cancelPendingBlurSave?.()
  }

  /**
   * Location step: one in-flight PATCH at a time; newer emissions cancel the previous HTTP call.
   */
  private _listrenToLocationChanges(): void {
    this._locationDetailsPatch$
      .pipe(
        switchMap((payload) => {
          const current = this.eventData()
          if (!current?.id) return EMPTY
          this.isSavingField.set(true)
          return this._eventService.updateCheckinEvent(current.workspace_id, current.id, payload.checkinId, payload.location_details).pipe(
            tap((response) => this._applyUpdatedCheckinToEventData(payload.checkinId, response.data)),
            catchError((error) => {
              this._alertService.alert(Alert.ERROR, error.message || 'Failed to update checkin event')
              return EMPTY
            }),
            finalize(() => this.isSavingField.set(false))
          )
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe()
  }

  /**
   * Apply the updated checkin to the event data
   * @param checkinId {number} - The checkin ID
   * @param updated {Checkin | undefined} - The updated checkin
   */
  private _applyUpdatedCheckinToEventData(checkinId: number, updated: Checkin | undefined): void {
    if (!updated) return
    const latest = this.eventData()
    if (!latest?.checkins?.length) return
    const checkins = latest.checkins.map((checkin) =>
      checkin.id === checkinId ? { ...checkin, ...updated, location_details: { ...checkin.location_details, ...updated.location_details } } : checkin
    )
    this.eventData.set({ ...latest, checkins })
  }

  /**
   * Save all unsaved changes (e.g. when user clicks "Save changes" in the unsaved modal), then stay in the wizard.
   * Rejects when Short Name is invalid/rejected so the wizard does not close with an empty slug.
   */
  public saveUnsavedChanges(): Observable<void> {
    this._eventIdentityRef?.flushPendingAutoSlugSave?.()
    if (this._eventIdentityRef?.hasShortNameError?.()) {
      this.onStepChanged(EventWizardStep.STEP_1)
      this._eventIdentityRef.revealShortNameRequired()
      return throwError(() => new Error('Event short name is invalid'))
    }

    const identityChanges = this._eventIdentityRef?.getUnsavedChanges?.() ?? []
    const locationChange = this._locationTimeZoneRef?.getUnsavedChanges?.()

    const observables: import('rxjs').Observable<unknown>[] = identityChanges.map((change) => this._updateEventDataObservable(change, { throwOnValidationError: true }))
    if (locationChange) {
      if (this.eventData()?.id) {
        this.isSavingField.set(true)
        observables.push(
          this._eventService.updateCheckinEvent(this.eventData()?.workspace_id, this.eventData()?.id, locationChange.checkinId, locationChange.location_details).pipe(
            tap((response) => {
              this._applyUpdatedCheckinToEventData(locationChange.checkinId, response.data)
              this.isSavingField.set(false)
            }),
            catchError((err) => {
              this.isSavingField.set(false)
              return throwError(() => err)
            })
          )
        )
      }
    }

    if (observables.length === 0) return of(void 0)
    return forkJoin(observables).pipe(
      map(() => void 0),
      catchError((err) => {
        this.onStepChanged(EventWizardStep.STEP_1)
        this._eventIdentityRef?.revealShortNameRequired?.()
        return throwError(() => err)
      })
    )
  }

  /**
   * Route-based close flow: save then navigate using the pending callback.
   */
  public saveChangesAndLeave(): void {
    this._wizardService
      .confirmRouteCloseWithSave(() => this.saveUnsavedChanges())
      .pipe(take(1))
      .subscribe()
  }

  /**
   * Route-based close flow: discard pending form edits and navigate.
   */
  public discardChangesAndLeave(): void {
    this._wizardService.discardRouteClose()
  }

  /**
   * Route-based close flow: user dismissed the warning modal (X/backdrop/escape).
   * Keep wizard open and drop pending navigation callback.
   */
  public closeUnsavedModalOnly(): void {
    this._wizardService.dismissRouteClose()
  }

  /** Queues a checkin `location_details` PATCH (serialized in `_subscribeLocationDetailsPatches`). */
  public onLocationDetailsChange(payload: CheckinLocationPatchPayload): void {
    const current = this.eventData()
    if (!current?.id) return
    this._locationDetailsPatch$.next(payload)
  }

  /**
   * Handle real-time title changes from the identity step
   * @param title {string} - The new event title
   */
  public onTitleChanged(title: string): void {
    const trimmedTitle = (title || '').trim()
    const breadcrumbs = trimmedTitle ? [trimmedTitle] : ['Event Name']
    this._wizardService.setBreadcrumbs(breadcrumbs)
  }

  public onFinish(): void {
    this._wizardService.requestCloseAndThen(null)
  }

  /**
   * Keep the wizard open when Event Name or Short Name is missing or invalid.
   * Cleared Short Name is restored to the previous saved/auto value (never saved as empty).
   * Also silent-saves a system-generated short name so Finish/X do not show the unsaved modal.
   * @returns true when close must be aborted
   */
  public blockWizardClose(): boolean {
    this._eventIdentityRef?.restoreClearedSlugOnClose?.()

    if (this._eventIdentityRef?.hasEventNameError()) {
      this.onStepChanged(EventWizardStep.STEP_1)
      this._eventIdentityRef.revealEventNameRequired()
      return true
    }

    if (this._eventIdentityRef?.hasShortNameError()) {
      this.onStepChanged(EventWizardStep.STEP_1)
      this._eventIdentityRef.revealShortNameRequired()
      return true
    }

    if (this._silentCloseInProgress) return true

    if (this._eventIdentityRef?.shouldSilentSaveAutoSlugOnClose?.()) {
      this._silentCloseInProgress = true
      this.saveUnsavedChanges()
        .pipe(take(1), takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => {
            this._silentCloseInProgress = false
            if (this._eventIdentityRef?.hasShortNameError()) {
              this.onStepChanged(EventWizardStep.STEP_1)
              this._eventIdentityRef.revealShortNameRequired()
              return
            }
            this._wizardService.requestCloseAndThen(null)
          },
          error: () => {
            this._silentCloseInProgress = false
            this.onStepChanged(EventWizardStep.STEP_1)
            this._eventIdentityRef?.revealShortNameRequired?.()
          }
        })
      return true
    }

    return false
  }
}
